'use client'

import { useId, useState, useMemo, useRef } from 'react'
import { XIcon } from 'lucide-react'
import * as PopoverPrimitive from '@radix-ui/react-popover'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from './command'
import { Label } from './label'
import { Popover } from './popover'
import { PortaledPopoverContent } from './portaled-popover-content'
import { useScrollableInPortal } from '../../hooks/use-scrollable-in-portal'
import { cn } from '../../utils'

export interface MultiSelectOption {
  value: string
  label: string
  description?: string | React.ReactNode
  disabled?: boolean
}

export interface MultiSelectProps {
  options: MultiSelectOption[]
  value: string[]
  onValueChange: (value: string[]) => void
  placeholder?: string
  label?: string | React.ReactNode
  id?: string
  searchable?: boolean
  hideSelectAll?: boolean
  disabled?: boolean
  required?: boolean
  error?: string
  searchPlaceholder?: string
  className?: string
  triggerClassName?: string
  maxHeight?: string
  emptyIndicator?: React.ReactNode
  side?: 'top' | 'bottom' | 'left' | 'right'
  /**
   * When provided, a "+ Add" link is rendered to the right of the
   * label.
   */
  onAddNew?: () => void
  /** Custom label for the inline-create link. Defaults to "+ Add". */
  addNewLabel?: string
}

export const MultiSelect = ({
  options,
  value,
  onValueChange,
  placeholder = 'Add...',
  label,
  id,
  disabled = false,
  required = false,
  error,
  className,
  side = 'bottom',
  onAddNew,
  addNewLabel = '+ Add',
}: MultiSelectProps) => {
  const generatedId = useId()
  const finalId = id || generatedId
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const portalScroll = useScrollableInPortal()

  const selectedOptions = useMemo(
    () => options.filter((opt) => value.includes(opt.value)),
    [options, value],
  )

  const filteredOptions = useMemo(() => {
    const unselected = options.filter(
      (opt) => !value.includes(opt.value) && !opt.disabled,
    )
    if (!search) return unselected
    const q = search.toLowerCase()
    return unselected.filter((opt) => opt.label.toLowerCase().includes(q))
  }, [options, value, search])

  const handleRemove = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation()
    onValueChange(value.filter((v) => v !== optionValue))
  }

  const handleSelect = (optionValue: string) => {
    onValueChange([...value, optionValue])
    setSearch('')
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !search && value.length > 0) {
      onValueChange(value.slice(0, -1))
    }
    if (e.key === 'Escape') {
      setOpen(false)
      inputRef.current?.blur()
    }
  }

  return (
    <div className={cn('w-full space-y-2', className)}>
      {(label || onAddNew) && (
        <div className="flex items-center justify-between">
          {label ? (
            <Label htmlFor={finalId}>
              {label}
              {required && <span className="text-destructive ml-1">*</span>}
            </Label>
          ) : (
            <span />
          )}
          {onAddNew && (
            <button
              type="button"
              onClick={onAddNew}
              className="text-xs text-primary hover:underline font-medium"
            >
              {addNewLabel}
            </button>
          )}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Anchor asChild>
          <div
            className={cn(
              'min-h-10 w-full rounded-md border border-border bg-card px-3 py-2 text-sm transition-colors',
              'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-0',
              error && 'border-destructive focus-within:ring-destructive',
              disabled && 'opacity-50 cursor-not-allowed',
            )}
            onClick={() => !disabled && inputRef.current?.focus()}
          >
            <div className="flex flex-wrap gap-1.5">
              {selectedOptions.map((opt) => (
                <span
                  key={opt.value}
                  className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-sm font-medium text-foreground"
                >
                  {opt.label}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={(e) => handleRemove(opt.value, e)}
                      className="ml-0.5 rounded text-muted-foreground hover:text-foreground transition-colors focus:outline-none"
                      aria-label={`Remove ${opt.label}`}
                    >
                      <XIcon size={12} />
                    </button>
                  )}
                </span>
              ))}
              <input
                ref={inputRef}
                id={finalId}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  if (!open) setOpen(true)
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={handleKeyDown}
                placeholder={value.length === 0 ? placeholder : ''}
                disabled={disabled}
                className="flex-1 min-w-[120px] bg-transparent outline-none border-0 shadow-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 placeholder:text-muted-foreground text-sm"
                aria-invalid={!!error}
                aria-required={required}
                aria-autocomplete="list"
                aria-expanded={open}
              />
            </div>
          </div>
        </PopoverPrimitive.Anchor>

        <PortaledPopoverContent
          side={side}
          className="w-full min-w-[var(--radix-popper-anchor-width)]"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onFocusOutside={(e) => {
            if (
              inputRef.current?.contains(e.target as Node) ||
              inputRef.current === e.target
            ) {
              e.preventDefault()
            }
          }}
          onInteractOutside={(e) => {
            if (
              inputRef.current?.contains(e.target as Node) ||
              inputRef.current === e.target
            ) {
              e.preventDefault()
            }
          }}
        >
          <Command filter={() => 1} className="flex flex-col min-h-0 flex-1">
            <CommandList ref={portalScroll} className="flex-1 min-h-0 max-h-none">
              {filteredOptions.length === 0 ? (
                <CommandEmpty className="py-3 text-sm text-center text-muted-foreground">
                  No options found
                </CommandEmpty>
              ) : (
                <CommandGroup>
                  {filteredOptions.map((opt) => (
                    <CommandItem
                      key={opt.value}
                      value={opt.value}
                      onSelect={() => handleSelect(opt.value)}
                      className="cursor-pointer"
                    >
                      {opt.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PortaledPopoverContent>
      </Popover>

      {error && (
        <p className="text-sm text-destructive mt-1" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
